/* dealHealth.js — Iter-281
 *
 * Deterministic Deal Health scoring engine for ChainFlow.
 *
 * Replaces the iter-207-era `severity` heuristic (Stable / Slipping
 * / At risk / Rising) with a transparent, weighted score that
 * classifies every deal as:
 *
 *   • `healthy`  (score ≥ 70)   — green   — feeds the "On Track" tab
 *   • `watch`    (score 40–69)  — amber   — feeds the "Watch" tab
 *   • `at_risk`  (score < 40)   — rose    — feeds the "At Risk" tab
 *
 * Signal weights — calibrated against the iter-281 brief:
 *
 *   • Critical blockers, missing Management Pack (leasehold),
 *     overdue milestones and exchange proximity carry the
 *     HIGHEST penalties.
 *   • Silent days are a SUPPORTING signal — a deal cannot tip into
 *     At Risk purely from being quiet for a few days.
 *   • Exchange ≤ 14 days with an active blocker is a SEVERE
 *     compound penalty.
 *   • Ready-for-exchange deals receive a meaningful positive bonus.
 *
 * The engine returns the score, the band, the severity colour token,
 * the raw signals, the per-signal contribution breakdown, AND a
 * human-readable `reasons` array of the top contributing factors
 * (sorted by absolute contribution, deterministically). Reasons are
 * pure, deterministic strings — never AI-generated.
 *
 * Pure module — zero React, zero side effects. Safe to call from
 * anywhere in the frontend (chainStore, page components, tests).
 *
 * Flag-gated at the integration sites: enable via
 * `REACT_APP_HEALTH_SCORING_V1 === "true"` in `frontend/.env`. The
 * engine itself always runs in tests; only the UI binding is
 * gated so we can sanity-check before flipping bands live.
 */

/* ─── Scoring weights ──────────────────────────────────────────────
 * Tuned per the iter-281 brief. Comments cite the brief verbatim
 * so the audit trail lives next to the numbers.
 */
export const HEALTH_WEIGHTS = Object.freeze({
  // "Overdue milestones … should carry the highest penalties."
  OVERDUE_DAYS:           4,   // max −84 (clamp days @ 21)
  EXCHANGE_OVERDUE_DAYS:  3,   // max −63 (clamp days @ 21)
  // "Missing Management Pack (leasehold) … highest penalties."
  MGMT_PACK_MISSING:     25,   // flat — only fires on leasehold + missing
  // Critical blocker = a rose-toned blocker.label in pilot data.
  CRITICAL_BLOCKER:      20,   // flat
  // "Exchange within 14 days with active blockers should
  //  significantly increase risk."
  IMMINENT_WITH_BLOCKER: 20,   // flat — fires when daysToExchange ≤ 14 AND active blocker
  // Operational backlog signals.
  OUTSTANDING_DOCS:       3,   // max −18 (clamp count @ 6)
  OUTSTANDING_ENQUIRIES:  2,   // max −10 (clamp count @ 5)
  MILESTONE_SLIP:         2,   // max −20 (slip ∈ {0, 5, 10})
  // "Silent days should be a supporting signal, not a primary driver."
  // "A deal should not become At Risk purely because it has been
  //  quiet for a few days."
  // Note: 14 silent days alone => 100 − 14 = 86 (still healthy).
  SILENT_DAYS:            1,   // max −14 (clamp days @ 14)
  BLOCKER_AGE_DAYS:       1.5, // max −15 (clamp days @ 10)
  // "Ready-for-exchange deals should receive a meaningful positive
  //  bonus."
  READY_BONUS:           20,   // flat positive

  // iter-285 — Participant accountability signals. Calibrated
  // against the iter-281 + iter-285 brief:
  //   "Track responsibility for buyers, sellers, solicitors,
  //    brokers and negotiators."
  //   "Feed participant responsibility into Health Scoring and
  //    Forecasting."
  //
  // A silent solicitor weighs measurably more than a silent
  // admin chase. The weights are clamped at age 14 so this
  // never tips a deal into At Risk on its own — the existing
  // critical-blocker + overdue-milestone signals still drive
  // the biggest penalties.
  SOLICITOR_UNRESPONSIVE_DAYS: 2,    // max −28 (× 14d cap)
  BROKER_UNRESPONSIVE_DAYS:    1.5,  // max −21 (× 14d cap)
  // Compound penalty when a legal participant is blocking an
  // imminent-exchange deal.
  LEGAL_BLOCKING_IMMINENT:    15,    // flat
});

/* ─── Band cutoffs ─────────────────────────────────────────────── */
export const HEALTH_BANDS = Object.freeze({
  HEALTHY_MIN: 70,
  AT_RISK_MAX: 39, // i.e. score < 40 → at_risk
});

/* ─── Severity colour tokens (Tailwind-friendly) ─────────────── */
export const HEALTH_SEVERITY = Object.freeze({
  healthy: "emerald",
  watch:   "amber",
  at_risk: "rose",
});

/* ─── Signal extraction ─────────────────────────────────────────
 *
 * `extractSignals(deal, ops)` derives every numeric / boolean
 * signal the scorer needs from the existing deal + ops payload.
 * Both inputs are tolerated — `ops` is optional (the page passes
 * the enriched `ops` object; tests can pass `deal` only).
 *
 * Every signal is deterministic per `deal.id`.
 */
export function extractSignals(deal, ops) {
  const d = deal || {};
  const o = ops || {};

  // Silent / wait days — the time since the deal last moved.
  const silentDays = Math.max(0, Math.min(14,
    Number(o.waitDays ?? d.waiting_days ?? d.waitingDays ?? 0)));

  // Overdue milestone days — current milestone target slipped past.
  const overdueDays = Math.max(0, Math.min(21,
    Number(o.overdueDays ?? 0)));

  // Exchange overdue — exchange date already past now.
  const dte = Number.isFinite(o.daysToExchange) ? o.daysToExchange : null;
  const exchangeOverdueDays = (dte != null && dte < 0)
    ? Math.min(21, Math.abs(dte))
    : 0;

  // Days to exchange (positive = future, negative = overdue).
  const daysToExchange = dte;

  // Tenure + Mgmt Pack outstanding (leasehold only).
  const tenureRaw = String(d.tenure || o.tenure || "").toLowerCase();
  const isLeasehold = tenureRaw === "leasehold";
  // Mgmt Pack is considered outstanding when the deal is leasehold AND
  // (a) the operator hasn't ticked it complete, OR
  // (b) the canonical blocker copy mentions a management pack.
  const completedSteps = Array.isArray(d.completedSteps) ? d.completedSteps : [];
  const mgmtPackComplete = completedSteps.some((s) =>
    /mgmt[\s_-]*pack|management\s*pack/i.test(String(s)));
  const blockerLabel = String(o.blocker?.label || o.blockerLabel || "");
  const blockerMentionsMgmtPack = /mgmt[\s_-]*pack|management\s*pack/i.test(blockerLabel);
  const mgmtPackMissing = isLeasehold && (!mgmtPackComplete && (blockerMentionsMgmtPack || !mgmtPackComplete));
  // Refined: only flag mgmt pack missing on leasehold deals that have
  // progressed past Contract (railActiveIdx ≥ 3 on the 8-node rail)
  // OR carry a blocker explicitly citing the management pack.
  const railIdx = Number(o.railActiveIdx ?? 0);
  const mgmtPackOutstanding = isLeasehold && (
    blockerMentionsMgmtPack || (!mgmtPackComplete && railIdx >= 3)
  );

  // Critical blocker — rose-toned blocker in the existing pilot data.
  const blockerTone = String(o.blocker?.tone || "").toLowerCase();
  const hasCriticalBlocker = blockerTone === "rose";

  // Active blocker (any tone) — drives the imminent-with-blocker
  // compound penalty.
  const hasActiveBlocker = !!(o.blocker && blockerLabel);

  // Imminent exchange = within the next 14 days (inclusive of today
  // and exchange overdue).
  const exchangeIsImminent = dte != null && dte <= 14;
  const imminentWithBlocker = exchangeIsImminent && hasActiveBlocker && !o.ready;

  // Outstanding documents — counts pending doc requests + flagged
  // documents missing in the deal payload.
  const docRequests = Array.isArray(d.docRequests) ? d.docRequests : [];
  const outstandingFromRequests = docRequests.filter((r) => {
    const status = String(r?.status || "").toLowerCase();
    return status === "requested" || status === "pending" || status === "outstanding";
  }).length;
  const outstandingDocs = Math.min(6, outstandingFromRequests);

  // Outstanding enquiries — canonical open enquiry records only (no synthetic counts).
  const _openEnq = Number(o.openEnquiries ?? d.openEnquiries ?? d.open_enquiries);
  const outstandingEnquiries = Number.isFinite(_openEnq) ? Math.max(0, Math.min(5, _openEnq)) : 0;

  // Milestone slip — coarse 3-state signal:
  //   0  → on plan
  //   5  → mildly behind (>= 7 days to exchange but rail < expected)
  //   10 → significantly behind (within 7 days of exchange but
  //         still earlier than Contract on the rail)
  let milestoneSlip = 0;
  if (dte != null && railIdx < 999) {
    // Expected rail index based on time remaining:
    //  • ≤ 3 days  → should be at Exchange (idx ≥ rail.length − 2)
    //  • ≤ 7 days  → should be past Enquiries
    //  • ≤ 14 days → should be past Searches
    const railLen = Array.isArray(o.railLabels) ? o.railLabels.length : 7;
    if (dte <= 3  && railIdx < railLen - 2)         milestoneSlip = 10;
    else if (dte <= 7  && railIdx < railLen - 3)    milestoneSlip = 5;
    else if (dte <= 14 && railIdx < railLen - 4)    milestoneSlip = 5;
  }

  // Blocker age — from the canonical blocker record only.
  const _ba = Number(o.blocker?.age_days ?? o.blocker?.days ?? o.blockerAgeDays);
  const blockerAgeDays = hasActiveBlocker && Number.isFinite(_ba)
    ? Math.max(0, Math.min(10, _ba))
    : 0;

  // Ready signal — set by the existing pilot logic when the deal is
  // exchange_ready / exchanged / completed.
  const ready = !!o.ready;

  // iter-285 — Participant accountability signals.
  //
  // When the consumer of `extractSignals` passes a pre-computed
  // participant envelope on `ops.participantSignals` (e.g. from
  // `participants.participantSignalsFor`), we surface those
  // signals on the returned object so the scorer can apply
  // role-specific penalties. When absent, the keys default to
  // safe zeros — the legacy ops-only scoring path is unaffected.
  const ps = (o && o.participantSignals) || {};
  const solicitorUnresponsiveDays = Math.max(0, Math.min(14, Number(ps.solicitorUnresponsiveDays ?? 0)));
  const brokerUnresponsiveDays    = Math.max(0, Math.min(14, Number(ps.brokerUnresponsiveDays    ?? 0)));
  const participantsBlocking      = Math.max(0, Number(ps.participantsBlocking ?? 0));
  const sellerSolicitorIsBlocking = !!ps.sellerSolicitorIsBlocking;
  const buyerSolicitorIsBlocking  = !!ps.buyerSolicitorIsBlocking;
  const brokerIsBlocking          = !!ps.brokerIsBlocking;
  const legalBlockingImminent =
    (sellerSolicitorIsBlocking || buyerSolicitorIsBlocking) &&
    (daysToExchange != null && daysToExchange <= 14 && daysToExchange >= -7);

  return {
    silentDays,
    overdueDays,
    exchangeOverdueDays,
    daysToExchange,
    isLeasehold,
    mgmtPackOutstanding,
    hasCriticalBlocker,
    hasActiveBlocker,
    imminentWithBlocker,
    outstandingDocs,
    outstandingEnquiries,
    milestoneSlip,
    blockerAgeDays,
    blockerLabel,
    ready,
    // iter-285 — participant attribution surface
    solicitorUnresponsiveDays,
    brokerUnresponsiveDays,
    participantsBlocking,
    sellerSolicitorIsBlocking,
    buyerSolicitorIsBlocking,
    brokerIsBlocking,
    legalBlockingImminent,
  };
}

/* ─── Reason labels (deterministic, human-readable) ───────────── */
function _labelFor(key, value, signals) {
  switch (key) {
    case "OVERDUE_DAYS":
      return value > 1 ? `Overdue milestone (${value}d)` : "Overdue milestone";
    case "EXCHANGE_OVERDUE_DAYS":
      return `Exchange overdue ${value}d`;
    case "MGMT_PACK_MISSING":
      return "Management Pack outstanding";
    case "CRITICAL_BLOCKER":
      return signals.blockerLabel
        ? `Critical blocker: ${signals.blockerLabel}`
        : "Critical blocker active";
    case "IMMINENT_WITH_BLOCKER":
      return signals.daysToExchange != null
        ? `Exchange in ${Math.max(0, signals.daysToExchange)}d with blocker`
        : "Exchange imminent with blocker";
    case "OUTSTANDING_DOCS":
      return value === 1 ? "Document outstanding" : `${value} docs outstanding`;
    case "OUTSTANDING_ENQUIRIES":
      return value === 1 ? "Enquiry outstanding" : `${value} enquiries outstanding`;
    case "MILESTONE_SLIP":
      return value >= 10 ? "Behind plan (severe)" : "Behind plan";
    case "SILENT_DAYS":
      return `Silent ${value}d`;
    case "BLOCKER_AGE_DAYS":
      return `Blocker ${value}d old`;
    case "READY_BONUS":
      return "Ready for exchange";
    case "SOLICITOR_UNRESPONSIVE_DAYS":
      // Brief: differentiate seller vs buyer when possible.
      return signals.sellerSolicitorIsBlocking
        ? `Seller Solicitor silent ${value}d`
        : signals.buyerSolicitorIsBlocking
          ? `Buyer Solicitor silent ${value}d`
          : `Solicitor silent ${value}d`;
    case "BROKER_UNRESPONSIVE_DAYS":
      return `Mortgage Broker silent ${value}d`;
    case "LEGAL_BLOCKING_IMMINENT":
      return "Solicitor blocking imminent exchange";
    default:
      return key;
  }
}

/* ─── Core scorer ─────────────────────────────────────────────── */
export function scoreDeal(deal, ops) {
  const signals = extractSignals(deal, ops);

  // Per-signal contributions. Each entry: { key, label, weight,
  // rawValue, contribution } where contribution is negative for
  // penalties and positive for the ready bonus.
  const contributions = [];

  function _push(key, rawValue, factor /* multiplier on the weight */) {
    if (!factor) return;
    const weight = HEALTH_WEIGHTS[key];
    const contribution = -(weight * factor);
    if (contribution === 0) return;
    contributions.push({
      key,
      label: _labelFor(key, rawValue, signals),
      weight,
      rawValue,
      contribution,
    });
  }

  _push("OVERDUE_DAYS",          signals.overdueDays,                       signals.overdueDays);
  _push("EXCHANGE_OVERDUE_DAYS", signals.exchangeOverdueDays,               signals.exchangeOverdueDays);
  _push("MGMT_PACK_MISSING",     signals.mgmtPackOutstanding ? 1 : 0,       signals.mgmtPackOutstanding ? 1 : 0);
  _push("CRITICAL_BLOCKER",      signals.hasCriticalBlocker ? 1 : 0,        signals.hasCriticalBlocker ? 1 : 0);
  _push("IMMINENT_WITH_BLOCKER", signals.imminentWithBlocker ? 1 : 0,       signals.imminentWithBlocker ? 1 : 0);
  _push("OUTSTANDING_DOCS",      signals.outstandingDocs,                   signals.outstandingDocs);
  _push("OUTSTANDING_ENQUIRIES", signals.outstandingEnquiries,              signals.outstandingEnquiries);
  _push("MILESTONE_SLIP",        signals.milestoneSlip,                     signals.milestoneSlip);
  _push("SILENT_DAYS",           signals.silentDays,                        signals.silentDays);
  _push("BLOCKER_AGE_DAYS",      signals.blockerAgeDays,                    signals.blockerAgeDays);
  // iter-285 — Participant accountability penalties.
  _push("SOLICITOR_UNRESPONSIVE_DAYS", signals.solicitorUnresponsiveDays, signals.solicitorUnresponsiveDays);
  _push("BROKER_UNRESPONSIVE_DAYS",    signals.brokerUnresponsiveDays,    signals.brokerUnresponsiveDays);
  _push("LEGAL_BLOCKING_IMMINENT",     signals.legalBlockingImminent ? 1 : 0, signals.legalBlockingImminent ? 1 : 0);

  // Ready bonus — POSITIVE contribution.
  if (signals.ready) {
    contributions.push({
      key:          "READY_BONUS",
      label:        _labelFor("READY_BONUS", 1, signals),
      weight:       HEALTH_WEIGHTS.READY_BONUS,
      rawValue:     1,
      contribution: +HEALTH_WEIGHTS.READY_BONUS,
    });
  }

  // Compose final score.
  let score = 100;
  for (const c of contributions) score += c.contribution;
  score = Math.max(0, Math.min(100, Math.round(score)));

  // Band assignment.
  let band;
  if (score >= HEALTH_BANDS.HEALTHY_MIN)      band = "healthy";
  else if (score <= HEALTH_BANDS.AT_RISK_MAX) band = "at_risk";
  else                                         band = "watch";

  const severity = HEALTH_SEVERITY[band];

  // Top reasons — sort contributions by |contribution| descending,
  // take the top 3 NEGATIVE ones (penalties). The ready bonus
  // stays in `breakdown` but doesn't surface as a "reason".
  const reasons = contributions
    .filter((c) => c.contribution < 0)
    .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution))
    .slice(0, 3)
    .map((c) => c.label);

  return {
    score,
    band,
    severity,
    signals,
    breakdown: contributions,
    reasons,
  };
}

/* ─── Confidence projection ───────────────────────────────────────
 *
 * Per the iter-281 spec, `health.score` REPLACES the legacy
 * `ops.confidence` (the 95 − waitDays × 2 heuristic). Confidence
 * is now identical to the health score so the Projection cell,
 * the Forecasting Centre (future) and the Operational Pulse all
 * read the same number.
 */
export function projectedConfidence(deal, ops) {
  return scoreDeal(deal, ops).score;
}

/* ─── iter-294 — Canonical Deal Health resolver ───────────────────
 *
 * Single source of truth for the deal-level status BAND across
 * every surface (Deal Room header pill, Deal Overview, Exchange
 * Eve card, Deal Rooms list counts, Forecasting Centre, Deal
 * Command Page hero, chainStore.derivedStatus). Removes the
 * historical drift where each surface used a different
 * threshold:
 *
 *   • DealRoomCanonicalPage header pill — `blockers.length < 2`
 *   • DealRoomCanonicalPage Exchange Eve — `blockers.length < 2`
 *   • DealCommandPage hero — `chainHealth < 60 → Progress Slowing`
 *   • chainStore.migrateDeal — `chainHealth < 70 → At Risk`
 *   • DealRoomsIndexPage list — `scoreDeal(deal).band`
 *
 * Post-iter-294 every surface routes through `dealStatusFor()`
 * which guarantees the same {band, severity} for the same deal
 * regardless of the renderer. Surface-specific *visual* copy
 * (e.g. "On track" vs "Moving Well") still varies per the design
 * brief — but the underlying band cannot diverge.
 *
 * Returned shape:
 *   {
 *     band:       "healthy" | "watch" | "at_risk",
 *     severity:   HEALTH_SEVERITY[band],     // colour token
 *     score:      number,                    // 0..100
 *     onTrack:    boolean,                   // band === "healthy"
 *     atRisk:     boolean,                   // band === "at_risk"
 *     terminal:   "exchanged" | "completed" | null,
 *     label: {
 *       canonical:    "On Track" | "Watch" | "At Risk",
 *       canonicalUpper: "ON TRACK" | "WATCH" | "AT RISK",
 *       hero:           "Moving Well" | "Requires Attention" | "Progress Slowing",
 *     },
 *     reasons:    string[],                  // top contributing penalties
 *   }
 *
 * Terminal deals (`stage` is "exchanged" or "completed") short-
 * circuit to the canonical "Completed" / "Exchanged" band tokens
 * so post-progression surfaces don't accidentally read as
 * "At Risk".
 */
export const STATUS_LABELS = Object.freeze({
  healthy:    { canonical: "On Track",   canonicalUpper: "ON TRACK", hero: "Moving Well" },
  watch:      { canonical: "Watch",      canonicalUpper: "WATCH",    hero: "Requires Attention" },
  at_risk:    { canonical: "At Risk",    canonicalUpper: "AT RISK",  hero: "Progress Slowing" },
  exchanged:  { canonical: "Exchanged",  canonicalUpper: "EXCHANGED", hero: "Exchanged" },
  completed:  { canonical: "Completed",  canonicalUpper: "COMPLETED", hero: "Completed" },
});

/* Shared classification rule (approved 2026-10-20). One source for Deal Rooms, Sidebar,
 * Flow Centre and the Deal Room header. Age alone never makes a deal At Risk. */
export const PROGRESS_THRESHOLDS = Object.freeze({ WATCH_DAYS: 7, ESCALATION_REVIEW_DAYS: 14 });

export function classifyDeal(deal) {
  const d = deal || {};
  const stage = String(d.stage || "").toLowerCase();
  const status = String(d.status || "").trim().toLowerCase();
  const none = { atRisk: false, watch: false, escalationReview: false, ready: false, needsAttention: false, reasons: [] };
  if (d.archived || stage === "completed" || stage === "fallen_through" || status === "completed" || status === "fallen through") return none;
  const exchanged = stage === "exchanged" || stage === "completion" || status === "exchanged";
  const open = (Array.isArray(d.blockers) ? d.blockers : []).filter((b) => b && !b.resolved && !b.resolved_at);
  const blocking = open.filter((b) => b.severity === "blocking");
  const warning = open.filter((b) => b.severity === "warning");
  const flagged = status === "at risk" || String(d.risk || "").toLowerCase() === "high";
  const milestoneBlocked = Object.values(d.milestoneStates || {}).some((m) => m && m.status === "blocked");
  const te = d.targetExchange || d.target_exchange;
  const teMs = te ? Date.parse(String(te).slice(0, 10) + "T00:00:00") : NaN;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const overdueDays = !exchanged && Number.isFinite(teMs) && teMs < today.getTime()
    ? Math.round((today.getTime() - teMs) / 86400000) : 0;
  // Inactivity: latest meaningful progression event, else Sale Agreed (backend inactive_days).
  const _w = Number(d.inactive_days ?? d.inactiveDays ?? d.waiting_days ?? d.waitingDays);
  const waitDays = !exchanged && Number.isFinite(_w) ? _w : 0;

  const reasons = [];
  if (flagged) reasons.push("At Risk (operator flagged)");
  if (milestoneBlocked) reasons.push("Milestone blocked");
  if (overdueDays > 0) reasons.push(`Exchange target overdue ${overdueDays}d`);
  if (blocking.length) reasons.push(blocking[0].title || blocking[0].label || "Blocking issue open");
  const atRisk = reasons.length > 0;
  const escalationReview = waitDays >= PROGRESS_THRESHOLDS.ESCALATION_REVIEW_DAYS;
  if (escalationReview) reasons.push(`No progress ${waitDays}d — escalation review`);
  else if (waitDays >= PROGRESS_THRESHOLDS.WATCH_DAYS) reasons.push(`No progress ${waitDays}d`);
  if (warning.length) reasons.push(warning[0].title || warning[0].label || "Open issue");
  const watch = !atRisk && (waitDays >= PROGRESS_THRESHOLDS.WATCH_DAYS || warning.length > 0);
  return {
    atRisk, watch, escalationReview,
    // All required, applicable pre-exchange milestones recorded (backend count); stage only as fallback.
    ready: !exchanged && (Number.isFinite(Number(d.pre_exchange_outstanding)) && d.pre_exchange_outstanding !== null
      ? Number(d.pre_exchange_outstanding) === 0 : stage === "exchange_ready"),
    needsAttention: atRisk || watch || blocking.length + warning.length > 0,
    reasons: reasons.slice(0, 3),
  };
}

export function dealStatusFor(deal, ops) {
  // Honour explicit terminal states. Once a deal is exchanged or
  // completed the rail cannot regress into "At Risk".
  const stage = String(deal?.stage || "").toLowerCase();
  if (stage === "completed") {
    return {
      band:     "healthy",
      severity: HEALTH_SEVERITY.healthy,
      score:    100,
      onTrack:  true,
      atRisk:   false,
      terminal: "completed",
      label:    STATUS_LABELS.completed,
      reasons:  [],
    };
  }
  if (stage === "exchanged") {
    return {
      band:     "healthy",
      severity: HEALTH_SEVERITY.healthy,
      score:    100,
      onTrack:  true,
      atRisk:   false,
      terminal: "exchanged",
      label:    STATUS_LABELS.exchanged,
      reasons:  [],
    };
  }
  /* iter-alignment · Health Score SSOT · 2026-03-10 ─────────────
   * Canonical operational Health = `deal.chain_health` (0-100).
   * `deal_health_score` is legacy and MUST NOT drive user-facing
   * bands.  `scoreDeal(deal, ops)` still runs so tooltips /
   * breakdown surfaces can expose the top penalty reasons — but
   * the band + score returned by `dealStatusFor` come from
   * `chain_health`, keeping every surface (Deal Rooms, Director
   * Centre, Command Centre, Mission Control, Flow Centre) on the
   * same SSOT number for the same deal.  Thresholds reuse the
   * existing `HEALTH_BANDS` cutoffs (HEALTHY_MIN=70, AT_RISK_MAX=39)
   * so approved On Track / Watch / At Risk copy stays intact.
   */
  const _rawCh = deal?.chain_health ?? deal?.chainHealth;
  const _chainHealth = Number.isFinite(Number(_rawCh))
    ? Math.max(0, Math.min(100, Math.round(Number(_rawCh))))
    : 100;

  let health;
  try {
    health = scoreDeal(deal, ops);
  } catch (_e) {
    health = { reasons: [] };
  }

  /* P0 · Deal Room Operational Coherence Audit (2026-02-28)
   * ---------------------------------------------------------
   * Canonical backend flags override the chain_health band.
   * `deal.status === "At Risk"` and `deal.risk === "High"`
   * are first-class operator + engine signals — a deal that
   * the backend / operator has explicitly flagged as at-risk
   * cannot upgrade above `at_risk` on any surface, regardless
   * of the chain_health integer.  The raw score is clamped
   * into the At Risk band so downstream renderers (list tabs,
   * count pills, KPI cards) stay coherent.  Never mutates the
   * deal.
   */
  const rawStatus = String(deal?.status || "").trim().toLowerCase();
  const rawRisk   = String(deal?.risk   || "").trim().toLowerCase();
  const backendAtRisk = rawStatus === "at risk" || rawRisk === "high";
  const backendFallenThrough = rawStatus === "fallen through";

  const _cls = classifyDeal(deal);
  let band = _cls.atRisk ? "at_risk" : _cls.watch ? "watch" : "healthy";
  let score = band === "at_risk" ? Math.min(_chainHealth, HEALTH_BANDS.AT_RISK_MAX)
    : band === "watch" ? Math.min(_chainHealth, HEALTH_BANDS.HEALTHY_MIN - 1) : Math.max(_chainHealth, HEALTH_BANDS.HEALTHY_MIN);
  let reasons = [..._cls.reasons, ...(Array.isArray(health.reasons) ? health.reasons : [])].slice(0, 3);

  // A pre-exchange deal past its exchange target is At Risk on every surface.
  const _te = deal?.targetExchange || deal?.target_exchange;
  const _teMs = _te ? Date.parse(String(_te).slice(0, 10) + "T00:00:00") : NaN;
  const _today = new Date(); _today.setHours(0, 0, 0, 0);
  const exchangeOverdueDays = Number.isFinite(_teMs) && _teMs < _today.getTime()
    ? Math.round((_today.getTime() - _teMs) / 86400000) : 0;
  if (exchangeOverdueDays > 0 && !backendFallenThrough) {
    band = "at_risk";
    score = Math.min(score, HEALTH_BANDS.AT_RISK_MAX);
    const r = `Exchange target overdue ${exchangeOverdueDays}d`;
    reasons = [r, ...reasons.filter((x) => !/^Exchange (target )?overdue/.test(x))].slice(0, 3);
  }

  if (backendFallenThrough || backendAtRisk) {
    band = "at_risk";
    // Clamp score into the At Risk band so any consumer that
    // reads the number (Forecasting Centre, list sort, KPI card)
    // stays consistent with the band.
    score = Math.min(score, HEALTH_BANDS.AT_RISK_MAX);
    // Surface WHY at the top of the reasons list so tooltips /
    // hero copy can explain the state without a second lookup.
    const canonicalReason = backendFallenThrough
      ? "Deal fallen through"
      : (rawRisk === "high" ? "High risk (operator flagged)" : "At Risk (operator flagged)");
    reasons = [canonicalReason, ...reasons.filter((r) => r !== canonicalReason)].slice(0, 3);
  }

  return {
    band,
    severity: HEALTH_SEVERITY[band],
    score,
    onTrack:  band === "healthy",
    atRisk:   band === "at_risk",
    terminal: null,
    label:    STATUS_LABELS[band],
    reasons,
  };
}

/* ─── Feature-flag helper ──────────────────────────────────────── */
export function isHealthScoringEnabled() {
  return String(
    (typeof process !== "undefined" && process.env && process.env.REACT_APP_HEALTH_SCORING_V1)
      || ""
  ).toLowerCase() === "true";
}
